import { useState, useCallback, useEffect } from 'react';
import { ProjectDto, CreateSubProjectRequest, projectsApi } from '../services/projects';

interface UseSubProjectsOptions {
  parentProjectId: string;
  autoLoad?: boolean;
}

interface UseSubProjectsReturn {
  subProjects: ProjectDto[];
  isLoading: boolean;
  error: string | null;
  loadSubProjects: () => Promise<void>;
  createSubProject: (data: CreateSubProjectRequest) => Promise<ProjectDto>;
  isCreating: boolean;
  completedCount: number;
  totalCount: number;
  progressPercentage: number;
}

export const useSubProjects = ({
  parentProjectId,
  autoLoad = false,
}: UseSubProjectsOptions): UseSubProjectsReturn => {
  const [subProjects, setSubProjects] = useState<ProjectDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSubProjects = useCallback(async () => {
    if (!parentProjectId) return;

    setIsLoading(true);
    setError(null);
    try {
      const projects = await projectsApi.getSubProjects(parentProjectId);
      setSubProjects(projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sub-projects');
    } finally {
      setIsLoading(false);
    }
  }, [parentProjectId]);

  const createSubProject = useCallback(async (data: CreateSubProjectRequest): Promise<ProjectDto> => {
    setIsCreating(true);
    setError(null);
    try {
      const newProject = await projectsApi.createSubProject(parentProjectId, data);
      setSubProjects((prev) => [...prev, newProject]);
      return newProject;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create sub-project';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  }, [parentProjectId]);

  // Auto-load if enabled
  useEffect(() => {
    if (autoLoad && parentProjectId) {
      loadSubProjects();
    }
  }, [autoLoad, parentProjectId, loadSubProjects]);

  // Calculate counts
  const completedCount = subProjects.filter((p) => p.status === 'Completed').length;
  const totalCount = subProjects.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return {
    subProjects,
    isLoading,
    error,
    loadSubProjects,
    createSubProject,
    isCreating,
    completedCount,
    totalCount,
    progressPercentage,
  };
};

export default useSubProjects;
